import { configuration } from '@src/entities/Alert/includes/configuration';
import { AlertActions, AlertEntity, DefaultAlertEntity, SaveAlertPayload } from '@src/entities/Alert/includes/types';
import { useSaveAlert } from '@src/entities/Alert/ui/hooks';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Modal as ModalComponent } from '@uhg-abyss/web/ui/Modal';
import { isEmpty } from 'lodash';
import React from 'react';

import { Form } from '../Form';

type ModalProps = {
  action?: AlertActions;
  currentEntity?: AlertEntity;
  isOpen?: boolean;
  refetch?: () => void;
  setCurrentEntity?: (entity: AlertEntity) => void;
  setIsOpen?: (open: boolean) => void;
};

/**
 * Modal: Alert
 *
 * Displays a modal dialog containing the alert form for creating or editing alert data.
 * Handles modal open/close state and save logic.
 */
export function Modal({
  action,
  currentEntity,
  isOpen = false,
  refetch = () => {},
  setCurrentEntity = () => {},
  setIsOpen = () => {},
}: ModalProps) {
  const { handleSave: handleSaveAlert } = useSaveAlert(action);

  /**
   * Closes the modal and resets the current entity to default values.
   */
  const handleClose = (): void => {
    setIsOpen(false);
    setCurrentEntity(DefaultAlertEntity);
  };

  /**
   * Saves the alert entity and closes the modal.
   */
  const handleSave = async (payload: SaveAlertPayload): Promise<void> => {
    await handleSaveAlert(payload, refetch);
    handleClose();
  };

  return (
    <ErrorHandler location="src/entities/Alert/ui/components/Modal/Modal.tsx">
      <ModalComponent
        isOpen={isOpen}
        onClose={handleClose}
        title={!isEmpty(currentEntity) ? `Edit ${configuration.singularName}` : `Create ${configuration.singularName}`}
      >
        <ModalComponent.Section>
          <Form currentEntity={currentEntity} handleClose={handleClose} handleSave={handleSave} />
        </ModalComponent.Section>
      </ModalComponent>
    </ErrorHandler>
  );
}
